import { ImageUploader } from "./ImageUploader";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { CuisineSelector } from "./CuisineSelector";
import { DishTypeSelector } from "./DishTypeSelector";
import { TagSelector } from "./TagSelector";
import { IngredientInput } from "./IngredientInput";
import { NutritionalFields } from "./NutritionalFields";
import { AvailabilitySelector } from "./AvailabilitySelector";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { useNavigate } from "react-router-dom";
import { validateNumericInput } from "./utilities/validateInput";
import { useState } from "react";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { PortionSizeInput } from "./PortionSizeInput";
import { Switch } from "@/components/ui/switch";

const DAYS_OF_WEEK = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday"
];

interface EditMealFormProps {
  formData: {
    name: string;
    price: string;
    description: string;
    images: string[];
    deliveryAvailable: boolean;
    deliveryFee: string;
    cuisine_type: string;
    meal_type: string;
    dietary_tags: string[];
    ingredients: string[];
    allergens: string[];
    calories: string;
    carbs: string;
    protein: string;
    fat: string;
    availabilitySlots: any[];
    available_portions: string;
    hasLimitedPortions: boolean;
    pickup_instructions: string;
    preparation_type: 'scheduled' | 'on_demand';
    prep_time_hours: string;
    cutoff_time: string;
    max_daily_orders: string;
    pickup_window_start: string;
    pickup_window_end: string;
    available_days: string[];
    portion_size_value: string;
    portion_size_unit: string;
    use_custom_pickup_address: boolean;
    pickup_address: string;
    pickup_city: string;
    pickup_state: string;
    pickup_postal_code: string;
  };
  onSubmit: () => void;
  onChange: (field: string, value: any) => void;
}

export const EditMealForm = ({ formData, onSubmit, onChange }: EditMealFormProps) => {
  const navigate = useNavigate();
  const [priceError, setPriceError] = useState<string | null>(null);
  const [deliveryFeeError, setDeliveryFeeError] = useState<string | null>(null);

  const handlePriceChange = (value: string) => {
    const error = validateNumericInput(value, 'price');
    setPriceError(error);
    onChange("price", value);
  };

  const handleDeliveryFeeChange = (value: string) => {
    const error = validateNumericInput(value, 'price');
    setDeliveryFeeError(error);
    onChange("deliveryFee", value);
  };

  return (
    <div className="space-y-6">
      <div className="space-y-2.5">
        <Label htmlFor="photos" className="text-black font-medium text-base">Photos *</Label>
        <ImageUploader
          images={formData.images}
          onImagesChange={(images) => onChange("images", images)}
        />
      </div>

      <div className="space-y-2.5">
        <Label htmlFor="name" className="text-black font-medium text-base">Meal Name *</Label>
        <Input
          id="name"
          value={formData.name}
          onChange={(e) => onChange("name", e.target.value)}
          placeholder="Enter meal name"
          className="h-12 rounded-xl border-[#E5E5E5] focus:border-black focus:ring-black placeholder:text-[#6B7280] bg-[#EDEDED]"
        />
      </div>

      <div className="space-y-2.5">
        <Label htmlFor="price" className="text-black font-medium text-base">Price ($) *</Label>
        <Input
          id="price"
          type="text"
          value={formData.price}
          onChange={(e) => handlePriceChange(e.target.value)}
          placeholder="0.00"
          className={`h-12 rounded-xl border-[#E5E5E5] focus:border-black focus:ring-black placeholder:text-[#6B7280] bg-[#EDEDED] ${
            priceError ? 'border-red-500 focus-visible:ring-red-500' : ''
          }`}
        />
        {priceError && (
          <p className="text-sm text-red-500 mt-1">{priceError}</p>
        )}
      </div>

      <div className="space-y-2.5">
        <Label htmlFor="description" className="text-black font-medium text-base">Description *</Label>
        <Textarea
          id="description"
          value={formData.description}
          onChange={(e) => onChange("description", e.target.value)}
          placeholder="Describe your meal"
          className="min-h-[120px] rounded-xl border-[#E5E5E5] focus:border-black focus:ring-black placeholder:text-[#6B7280] bg-[#EDEDED]"
        />
      </div>

      <div className="space-y-4">
        <Label className="text-black text-base">Preparation Type</Label>
        <RadioGroup
          value={formData.preparation_type}
          onValueChange={(value: 'scheduled' | 'on_demand') => onChange("preparation_type", value)}
          className="flex flex-col sm:grid sm:grid-cols-2 gap-4"
        >
          <div className="flex flex-col space-y-2 border rounded-xl p-6 hover:border-black transition-colors cursor-pointer">
            <div className="flex items-start space-x-3">
              <RadioGroupItem value="scheduled" id="scheduled" />
              <div className="flex-1">
                <Label htmlFor="scheduled" className="cursor-pointer">
                  <div className="font-medium text-lg mb-1">Scheduled</div>
                  <p className="text-sm text-muted-foreground">
                    Set specific dates and times when the meal will be available
                  </p>
                </Label>
              </div>
            </div>
          </div>
          <div className="flex flex-col space-y-2 border rounded-xl p-6 hover:border-black transition-colors cursor-pointer">
            <div className="flex items-start space-x-3">
              <RadioGroupItem value="on_demand" id="on_demand" />
              <div className="flex-1">
                <Label htmlFor="on_demand" className="cursor-pointer">
                  <div className="font-medium text-lg mb-1">On Demand</div>
                  <p className="text-sm text-muted-foreground">
                    Prepare meals after orders are placed with advance notice
                  </p>
                </Label>
              </div>
            </div>
          </div>
        </RadioGroup>
      </div>

      {formData.preparation_type === "scheduled" ? (
        <div className="space-y-2.5">
          <AvailabilitySelector
            selectedSlots={formData.availabilitySlots}
            onSlotsChange={(slots) => onChange("availabilitySlots", slots)}
            hasLimitedPortions={formData.hasLimitedPortions}
            availablePortions={formData.available_portions}
            onHasLimitedPortionsChange={(value) => onChange("hasLimitedPortions", value)}
            onAvailablePortionsChange={(value) => onChange("available_portions", value)}
          />
        </div>
      ) : (
        <div className="space-y-8">
          <div className="grid gap-6 sm:grid-cols-2">
            <div className="space-y-4">
              <Label className="text-black">Preparation Time (hours)</Label>
              <Input
                type="number"
                value={formData.prep_time_hours}
                onChange={(e) => onChange("prep_time_hours", e.target.value)}
                placeholder="e.g., 48"
                min="1"
                className="h-12 rounded-xl border-[#E5E5E5] bg-[#EDEDED]"
              />
              <p className="text-sm text-muted-foreground">
                Hours needed to prepare after receiving an order
              </p>
            </div>

            <div className="space-y-4">
              <Label className="text-black">Maximum Daily Orders</Label>
              <Input
                type="number"
                value={formData.max_daily_orders}
                onChange={(e) => onChange("max_daily_orders", e.target.value)}
                placeholder="e.g., 5"
                min="1"
                className="h-12 rounded-xl border-[#E5E5E5] bg-[#EDEDED]"
              />
            </div>
          </div>

          <div className="grid gap-6 sm:grid-cols-2">
            <div className="space-y-4">
              <Label className="text-black">Daily Order Cutoff Time</Label>
              <Input
                type="time"
                value={formData.cutoff_time}
                onChange={(e) => onChange("cutoff_time", e.target.value)}
                className="h-12 rounded-xl border-[#E5E5E5] bg-[#EDEDED]"
              />
              <p className="text-sm text-muted-foreground">
                Last time to place orders for next day
              </p>
            </div>

            <div className="space-y-4">
              <Label className="text-black">Pickup Window</Label>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Input
                    type="time"
                    value={formData.pickup_window_start}
                    onChange={(e) => onChange("pickup_window_start", e.target.value)}
                    className="h-12 rounded-xl border-[#E5E5E5] bg-[#EDEDED]"
                  />
                </div>
                <div>
                  <Input
                    type="time"
                    value={formData.pickup_window_end}
                    onChange={(e) => onChange("pickup_window_end", e.target.value)}
                    className="h-12 rounded-xl border-[#E5E5E5] bg-[#EDEDED]"
                  />
                </div>
              </div>
              <p className="text-sm text-muted-foreground">
                Time window when customers can pick up their order
              </p>
            </div>
          </div>

          <div className="space-y-4">
            <Label className="text-black">Available Days</Label>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {DAYS_OF_WEEK.map((day) => (
                <div key={day} className="flex items-center space-x-2">
                  <Checkbox
                    id={day}
                    checked={formData.available_days?.includes(day)}
                    onCheckedChange={(checked) => {
                      const currentDays = formData.available_days || [];
                      onChange(
                        "available_days",
                        checked
                          ? [...currentDays, day]
                          : currentDays.filter((d) => d !== day)
                      );
                    }}
                  />
                  <Label htmlFor={day}>{day}</Label>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Custom Pickup Address Section */}
      <div className="space-y-4 border-t pt-8">
        <div className="flex items-center justify-between">
          <Label htmlFor="customAddressToggle" className="text-base font-medium text-black">
            Use a custom pickup address for this meal
          </Label>
          <Switch
            id="customAddressToggle"
            checked={formData.use_custom_pickup_address}
            onCheckedChange={(checked) => onChange("use_custom_pickup_address", checked)}
          />
        </div>
        
        {formData.use_custom_pickup_address && (
          <div className="space-y-4 mt-4 p-4 bg-gray-50 rounded-xl border border-gray-200">
            <div className="space-y-2.5">
              <Label htmlFor="pickup_address" className="text-black">Street Address *</Label>
              <Input
                id="pickup_address"
                value={formData.pickup_address || ""}
                onChange={(e) => onChange("pickup_address", e.target.value)}
                placeholder="Enter street address"
                className="h-12 rounded-xl border-[#E5E5E5] bg-[#EDEDED]"
              />
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="space-y-2.5">
                <Label htmlFor="pickup_city" className="text-black">City *</Label>
                <Input
                  id="pickup_city"
                  value={formData.pickup_city || ""}
                  onChange={(e) => onChange("pickup_city", e.target.value)}
                  placeholder="City"
                  className="h-12 rounded-xl border-[#E5E5E5] bg-[#EDEDED]"
                />
              </div>
              
              <div className="space-y-2.5">
                <Label htmlFor="pickup_state" className="text-black">State *</Label>
                <Input
                  id="pickup_state"
                  value={formData.pickup_state || ""}
                  onChange={(e) => onChange("pickup_state", e.target.value)}
                  placeholder="State"
                  className="h-12 rounded-xl border-[#E5E5E5] bg-[#EDEDED]"
                />
              </div>
              
              <div className="space-y-2.5">
                <Label htmlFor="pickup_postal_code" className="text-black">ZIP Code *</Label>
                <Input
                  id="pickup_postal_code"
                  value={formData.pickup_postal_code || ""}
                  onChange={(e) => onChange("pickup_postal_code", e.target.value)}
                  placeholder="ZIP Code"
                  className="h-12 rounded-xl border-[#E5E5E5] bg-[#EDEDED]"
                />
              </div>
            </div>
            
            <p className="text-sm text-muted-foreground">
              This address will be used instead of your profile address for customer pickups.
            </p>
          </div>
        )}
        
        {!formData.use_custom_pickup_address && (
          <p className="text-sm text-muted-foreground mt-2">
            Your profile address will be used for customer pickups.
          </p>
        )}
      </div>

      <div className="space-y-4 border-t pt-8">
        <div className="flex items-center space-x-2">
          <Checkbox
            id="deliveryAvailable"
            checked={formData.deliveryAvailable}
            onCheckedChange={(checked) => onChange("deliveryAvailable", checked)}
          />
          <Label htmlFor="deliveryAvailable" className="text-sm font-medium text-black">
            Delivery available
          </Label>
        </div>

        {formData.deliveryAvailable && (
          <div className="space-y-2 ml-6">
            <Label htmlFor="deliveryFee" className="text-black">
              Delivery Fee ($)
            </Label>
            <Input
              id="deliveryFee"
              type="number"
              value={formData.deliveryFee}
              onChange={(e) => handleDeliveryFeeChange(e.target.value)}
              placeholder="Enter delivery fee"
              min="0"
              step="0.01"
              className="h-12 rounded-xl border-[#E5E5E5] bg-[#EDEDED] max-w-[200px]"
            />
            {deliveryFeeError && (
              <p className="text-sm text-red-500 mt-1">{deliveryFeeError}</p>
            )}
          </div>
        )}
      </div>

      <div className="space-y-2">
        <Label htmlFor="pickupInstructions" className="text-black">
          Pickup Instructions
        </Label>
        <Textarea
          id="pickupInstructions"
          value={formData.pickup_instructions}
          onChange={(e) => onChange("pickup_instructions", e.target.value)}
          placeholder="Enter instructions for customers picking up their order (e.g., 'Ring doorbell, pickup at side entrance')"
          className="min-h-[100px] rounded-xl border-[#E5E5E5] bg-[#EDEDED]"
        />
      </div>

      <div className="space-y-2.5">
        <Label className="text-black font-medium text-base">Dish Type *</Label>
        <DishTypeSelector
          dishType={formData.meal_type}
          onDishTypeChange={(type) => onChange("meal_type", type)}
        />
      </div>

      <div className="space-y-2.5">
        <Label className="text-black font-medium text-base">Cuisine Type *</Label>
        <CuisineSelector
          cuisineType={formData.cuisine_type}
          onCuisineChange={(cuisine) => onChange("cuisine_type", cuisine)}
        />
      </div>

      <div className="space-y-2.5">
        <Label className="text-black font-medium text-base">Dietary Tags</Label>
        <TagSelector
          label="dietary tags"
          selectedTags={formData.dietary_tags}
          onTagsChange={(tags) => onChange("dietary_tags", tags)}
          variant="default"
        />
      </div>

      <div className="space-y-2.5">
        <Label className="text-black font-medium text-base">Allergens</Label>
        <TagSelector
          label="allergens"
          selectedTags={formData.allergens}
          onTagsChange={(allergens) => onChange("allergens", allergens)}
          variant="destructive"
        />
      </div>

      <div className="space-y-2.5">
        <Label className="text-black font-medium text-base">Ingredients *</Label>
        <IngredientInput
          ingredients={formData.ingredients}
          onAddIngredient={(ingredient: string) => 
            onChange("ingredients", [...formData.ingredients, ingredient])
          }
          onRemoveIngredient={(ingredient: string) => 
            onChange("ingredients", formData.ingredients.filter(i => i !== ingredient))
          }
        />
      </div>

      <div className="space-y-2.5">
        <Label className="text-black font-medium text-xl">Nutritional Information</Label>
        <NutritionalFields
          calories={formData.calories}
          carbs={formData.carbs}
          protein={formData.protein}
          fat={formData.fat}
          onChange={onChange}
        />
      </div>

      <div className="space-y-2.5">
        <PortionSizeInput
          value={formData.portion_size_value}
          unit={formData.portion_size_unit || 'servings'}
          onValueChange={(value) => onChange("portion_size_value", value)}
          onUnitChange={(unit) => onChange("portion_size_unit", unit)}
        />
      </div>

      <div className="flex justify-between pt-4">
        <Button
          variant="outline"
          onClick={() => navigate('/seller/meals')}
          className="px-8 py-3 h-12 rounded-full text-base font-medium border-[#E5E5E5] hover:bg-[#F9FAFB] text-black"
        >
          Cancel
        </Button>
        <Button
          onClick={onSubmit}
          className="bg-black hover:bg-black/90 text-white px-8 py-3 h-12 rounded-full text-base font-medium transition-colors"
        >
          Update
        </Button>
      </div>
    </div>
  );
};
